import React, { useState, useMemo, useCallback } from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import LineChart from '../components/LineChart';
import { Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TrackRecordSection = () => {
  const { trackRecord } = useDashboard();
  const [timeFilter, setTimeFilter] = useState('all');

  // Memoize filtered trades to prevent unnecessary recalculations
  const filteredTrades = useMemo(() => {
    return trackRecord.filter((trade) => {
      if (timeFilter === 'all') return true;
      
      const tradeDate = new Date(trade.date);
      const now = new Date();
      
      switch(timeFilter) {
        case '1m': {
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(now.getMonth() - 1);
          return tradeDate >= oneMonthAgo;
        }
        case '3m': {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(now.getMonth() - 3);
          return tradeDate >= threeMonthsAgo;
        }
        case '6m': {
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(now.getMonth() - 6);
          return tradeDate >= sixMonthsAgo;
        }
        case '1y': {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(now.getFullYear() - 1);
          return tradeDate >= oneYearAgo;
        }
        case '2y': {
          const twoYearsAgo = new Date();
          twoYearsAgo.setFullYear(now.getFullYear() - 2);
          return tradeDate >= twoYearsAgo;
        }
        case '2024':
          return trade.date.startsWith('2024');
        case '2025':
          return trade.date.startsWith('2025');
        default:
          return true;
      }
    });
  }, [trackRecord, timeFilter]);

  // Memoize statistics calculations
  const stats = useMemo(() => {
    const totalTrades = filteredTrades.length;
    const winningTrades = filteredTrades.filter((t) => t.profit_loss > 0).length;
    const losingTrades = totalTrades - winningTrades;
    const winRate = totalTrades > 0 ? ((winningTrades / totalTrades) * 100).toFixed(1) : 0;
    const totalPnL = filteredTrades.reduce((sum, t) => sum + t.profit_loss, 0);
    const avgWin = winningTrades > 0 ? (filteredTrades.filter(t => t.profit_loss > 0).reduce((sum, t) => sum + t.profit_loss, 0) / winningTrades).toFixed(2) : 0;
    const avgLoss = losingTrades > 0 ? (filteredTrades.filter(t => t.profit_loss < 0).reduce((sum, t) => sum + t.profit_loss, 0) / losingTrades).toFixed(2) : 0;

    return { totalTrades, winningTrades, losingTrades, winRate, totalPnL, avgWin, avgLoss };
  }, [filteredTrades]);

  // Memoize asset-based equity data (grouped by asset)
  const equityData = useMemo(() => {
    // Group trades by asset and calculate cumulative gains
    const assetPerformance = {};
    
    filteredTrades.forEach((trade) => {
      if (!assetPerformance[trade.asset]) {
        assetPerformance[trade.asset] = {
          totalGain: 0,
          trades: 0
        };
      }
      assetPerformance[trade.asset].totalGain += trade.profit_loss;
      assetPerformance[trade.asset].trades += 1;
    });

    // Convert to arrays for chart
    const assets = Object.keys(assetPerformance);
    const gains = assets.map(asset => assetPerformance[asset].totalGain);
    
    // Sort by gains
    const sorted = assets.map((asset, i) => ({ asset, gain: gains[i] }))
      .sort((a, b) => b.gain - a.gain);

    return {
      assetLabels: sorted.map(item => item.asset),
      assetGains: sorted.map(item => item.gain),
      sortedTrades: [...filteredTrades].sort((a, b) => new Date(a.date) - new Date(b.date))
    };
  }, [filteredTrades]);

  // Get filter label for display
  const getFilterLabel = useCallback((filter) => {
    const labels = {
      '1m': 'Last Month',
      '3m': 'Last 3 Months',
      '6m': 'Last 6 Months',
      '1y': 'Last Year',
      '2y': 'Last 2 Years',
      'all': 'All Time',
      '2024': '2024',
      '2025': '2025'
    };
    return labels[filter] || 'All Time';
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-4xl font-bold text-slate-900 mb-2">Track Record</h2>
          <p className="text-slate-600">Complete trading history and performance analytics</p>
        </div>
        <div className="flex gap-2">
          <Select data-testid="time-filter-select" value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-48">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Last Month</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
              <SelectItem value="2y">Last 2 Years</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Performance Period Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-900">
          Showing performance for: <span className="font-semibold">{getFilterLabel(timeFilter)}</span>
          {filteredTrades.length > 0 && (
            <span className="ml-2 text-blue-700">
              ({new Date(Math.min(...filteredTrades.map(t => new Date(t.date)))).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {new Date(Math.max(...filteredTrades.map(t => new Date(t.date)))).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
            </span>
          )}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <p className="kpi-label mb-2">Total Trades</p>
          <p className="kpi-number">{stats.totalTrades}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <p className="kpi-label mb-2">Win Rate</p>
          <p className="kpi-number positive-value">{stats.winRate}%</p>
          <p className="text-sm text-slate-600 mt-2">{stats.winningTrades}W / {stats.losingTrades}L</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <p className="kpi-label mb-2">Total P&L</p>
          <p className={`kpi-number ${stats.totalPnL >= 0 ? 'positive-value' : 'negative-value'}`}>${stats.totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <p className="kpi-label mb-2">Avg Win/Loss</p>
          <p className="text-2xl font-bold text-slate-900 financial-number">
            <span className="positive-value">${stats.avgWin}</span> / <span className="negative-value">${Math.abs(stats.avgLoss)}</span>
          </p>
        </div>
      </div>

      {/* Asset Performance Chart (replaced Equity Curve) */}
      {equityData.assetLabels.length > 0 ? (
        <LineChart
          title="Asset Performance"
          data={equityData.assetGains}
          labels={equityData.assetLabels}
          color="#10B981"
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-12 text-center">
          <p className="text-slate-500 text-lg">No trades found for the selected period</p>
          <p className="text-slate-400 text-sm mt-2">Try selecting a different time range</p>
        </div>
      )}

      {/* Trade Log Table - Date column removed */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Trade Log</h3>
          <span className="text-sm text-slate-500">{filteredTrades.length} trades</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="trade-log-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 chart-label">Asset</th>
                <th className="text-right py-3 px-4 chart-label">Entry</th>
                <th className="text-right py-3 px-4 chart-label">Exit</th>
                <th className="text-right py-3 px-4 chart-label">Stop</th>
                <th className="text-right py-3 px-4 chart-label">P&L</th>
                <th className="text-center py-3 px-4 chart-label">Status</th>
              </tr>
            </thead>
            <tbody>
              {equityData.sortedTrades.slice(0, 20).map((trade) => (
                <tr key={trade.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{trade.asset}</td>
                  <td className="text-right py-3 px-4 table-number text-slate-900">${trade.entry.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right py-3 px-4 table-number text-slate-900">${trade.exit ? trade.exit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                  <td className="text-right py-3 px-4 table-number text-slate-900">${trade.stop.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className={`text-right py-3 px-4 table-number font-medium ${
                    trade.profit_loss > 0 ? 'positive-value' : 'negative-value'
                  }`}>
                    ${trade.profit_loss.toFixed(2)}
                  </td>
                  <td className="text-center py-3 px-4">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                      trade.status === 'closed' ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {trade.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredTrades.length > 20 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-slate-500">Showing 20 of {filteredTrades.length} trades</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackRecordSection;